import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, CheckBadgeIcon, StarIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import Button from '../common/Button';
import Logo from '../common/Logo';

export interface RewardData {
  name: string;
  tier: string;
  duration_months: number;
  description: string;
  source: string; // "joining_bonus" | "referral_bonus"
}

interface RewardUnlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
  rewardData: RewardData;
}

const PRO_BENEFITS = [
  { icon: '📸', label: '20 Portfolio Images' },
  { icon: '🚀', label: 'Create 10 Collaboration Posts' },
  { icon: '🖼️', label: 'Up to 10 images per Collaboration Post' },
  { icon: '📁', label: 'Manage 10 Active CRM Projects' },
  { icon: '👥', label: 'Manage up to 10 Leads / mo' },
  { icon: '🖼️', label: 'Client Proofing Galleries (100 images)' },
  { icon: '📝', label: 'Custom Contract Templates' },
  { icon: '🎨', label: 'Custom Branding on Documents' },
  { icon: '🤝', label: '10 Collaboration Requests / mo' },
  { icon: '🔔', label: 'Smart Email Notifications & Reminders' },
  { icon: '🔍', label: 'Priority Listing in Explore' },
];

const RewardUnlockedModal: React.FC<RewardUnlockedModalProps> = ({ isOpen, onClose, rewardData }) => {
  const isJoiningBonus = rewardData.source === 'joining_bonus';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-400"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-500"
              enterFrom="opacity-0 scale-90 translate-y-8"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-3xl bg-white dark:bg-gray-900 text-left align-middle shadow-2xl transition-all border border-orange-200/50 dark:border-orange-800/50">
                
                {/* Header with orange gradient */}
                <div className="relative bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 px-6 pt-8 pb-12 text-center overflow-hidden">
                  {/* Decorative circles */}
                  <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
                  <div className="absolute top-4 right-4 w-20 h-20 bg-white/10 rounded-full" />
                  <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />
                  
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/70 hover:text-white focus:outline-none z-10"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>

                  {/* Logo */}
                  <div className="relative z-10">
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-4 ring-4 ring-white/30">
                      <Logo className="h-12 w-12 rounded-full" />
                    </div>
                    
                    <h2 className="text-2xl font-black text-white tracking-tight mb-1">
                      🎉 Reward Unlocked!
                    </h2>
                    <p className="text-white/80 text-sm font-medium">
                      You've completed all checklist items
                    </p>
                  </div>
                </div>

                {/* Reward Details Card - overlapping the header */}
                <div className="px-6 -mt-6 relative z-10">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-5 mb-4">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 flex items-center justify-center">
                        {isJoiningBonus ? (
                          <RocketLaunchIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                        ) : (
                          <StarIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                          {rewardData.name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {rewardData.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Duration badge */}
                    <div className="flex items-center gap-2 bg-orange-50 dark:bg-orange-900/20 rounded-xl px-4 py-3 border border-orange-100 dark:border-orange-800/50">
                      <CheckBadgeIcon className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                      <span className="text-sm font-semibold text-orange-800 dark:text-orange-300">
                        {rewardData.duration_months} Month{rewardData.duration_months > 1 ? 's' : ''} of{' '}
                        <span className="uppercase">{rewardData.tier}</span> — FREE
                      </span>
                    </div>
                  </div>
                </div>

                {/* Benefits */}
                <div className="px-6 pb-2">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
                    Your Pro Benefits
                  </h4>
                  <div className="space-y-2">
                    {PRO_BENEFITS.map((benefit, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50"
                      >
                        <span className="text-lg">{benefit.icon}</span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {benefit.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer CTA */}
                <div className="p-6 pt-4">
                  <Button
                    variant="primary"
                    className="w-full justify-center bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white border-none shadow-lg shadow-orange-500/20 py-3 text-base font-bold rounded-xl"
                    onClick={onClose}
                  >
                    Start Using Pro ✨
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default RewardUnlockedModal;
